import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from '../context/FormContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CTAButton from '../components/CTAButton';
import SecurityBadges from '../components/SecurityBadges';

const AddressData: React.FC = () => {
  const { formData, updateFormData, nextStep } = useFormContext();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<{
    country?: string;
    city?: string;
    postalCode?: string;
  }>({});
  const [touched, setTouched] = useState<{
    country: boolean;
    city: boolean;
    postalCode: boolean;
  }>({
    country: false,
    city: false,
    postalCode: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate form fields when they change and have been touched
  useEffect(() => {
    if (touched.country || touched.city || touched.postalCode) {
      validateForm(false);
    }
  }, [formData.country, formData.city, formData.postalCode, touched]);

  const validateForm = (isSubmitting = true) => {
    const newErrors: {
      country?: string;
      city?: string;
      postalCode?: string;
    } = {};
    
    // Only validate fields that have been touched or if we're submitting the form
    if (isSubmitting || touched.country) {
      if (!formData.country) {
        newErrors.country = 'Bitte wählen Sie ein Land';
      }
    }
    
    if (isSubmitting || touched.city) {
      if (!formData.city.trim()) {
        newErrors.city = 'Bitte geben Sie die Stadt ein';
      }
    }
    
    // Validate postal code if it's not empty and has been touched or we're submitting
    if ((isSubmitting || touched.postalCode) && formData.postalCode) {
      if (!/^\d{5}$/.test(formData.postalCode)) {
        newErrors.postalCode = 'Die Postleitzahl muss aus 5 Ziffern bestehen';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      country: true,
      city: true,
      postalCode: true
    });
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        await nextStep({
          country: formData.country,
          city: formData.city,
          postalCode: formData.postalCode
        });
        navigate('/known-info');
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    navigate('/personal-data');
  };

  // Handle postal code input to only allow 5 digits
  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Only allow digits and limit to 5 characters
    const sanitizedValue = value.replace(/\D/g, '').slice(0, 5);
    
    updateFormData('postalCode', sanitizedValue);
  };

  // Mark field as touched when it loses focus
  const handleBlur = (field: 'country' | 'city' | 'postalCode') => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  // Get input class based on error state
  const getInputClass = (field: 'country' | 'city' | 'postalCode') => {
    return errors[field] && touched[field] 
      ? "input-field border-red-500 focus:border-red-500" 
      : "input-field";
  };

  // Get select class based on error state
  const getSelectClass = (field: 'country') => {
    return errors[field] && touched[field] 
      ? "select-field border-red-500 focus:border-red-500" 
      : "select-field";
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8 max-w-3xl">
        <div className="form-container fade-in">
          <h2 className="text-2xl text-center font-semibold text-deep-blue mb-8">
            Angaben zum letzten bekannten Wohnort:
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                Land:
              </label>
              <select
                id="country"
                className={getSelectClass('country')}
                value={formData.country}
                onChange={(e) => updateFormData('country', e.target.value)}
                onBlur={() => handleBlur('country')}
              >
                <option value="">Bitte auswählen</option>
                <option value="Deutschland">Deutschland</option>
              </select>
              {errors.country && touched.country && (
                <p className="mt-1 text-sm text-red-600">{errors.country}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  Stadt:
                </label>
                <input
                  type="text"
                  id="city"
                  placeholder="München"
                  className={getInputClass('city')}
                  value={formData.city}
                  onChange={(e) => updateFormData('city', e.target.value)}
                  onBlur={() => handleBlur('city')}
                />
                {errors.city && touched.city && (
                  <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Postleitzahl (optional):
                </label>
                <input
                  type="text"
                  id="postalCode"
                  placeholder="80539"
                  className={getInputClass('postalCode')}
                  value={formData.postalCode}
                  onChange={handlePostalCodeChange}
                  onBlur={() => handleBlur('postalCode')}
                  inputMode="numeric"
                  pattern="\d{5}"
                  maxLength={5}
                />
                {errors.postalCode && touched.postalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>
                )}
              </div>
            </div>
            
            <div className="mt-8 flex justify-between">
              <CTAButton 
                type="button"
                text="Zurück" 
                back={true}
                onClick={handleBack}
                className="w-[48%]" 
              />
              <CTAButton 
                type="submit"
                text="Weiter" 
                arrow={true}
                className={`w-[48%] ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              />
            </div>
          </form>
        </div>
        
        <SecurityBadges />
      </main>
      
      <Footer />
    </div>
  );
};

export default AddressData;
